import { Component, ViewChild, Input, OnChanges, SimpleChanges, ElementRef } from '@angular/core';

import {
  AllCommunityModule,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridSizeChangedEvent,
  Module,
  RowNode,
} from 'ag-grid-community';

import { HeaderCellRendererComponent } from './header-cell-renderer/header-cell-renderer.component';
import {
  CellRendererComponent,
  OnRowGroupToggledParams,
  GetIconClassCallbackParams,
} from './cell-renderer/cell-renderer.component';

@Component({
  selector: 'app-dynamic-table',
  templateUrl: './dynamic-table.component.html',
  styleUrls: ['./dynamic-table.component.scss'],
})
export class DynamicTableComponent implements OnChanges {
  @Input() title = 'Soti-Table';
  @Input() tableRawData: any;
  @Input() hasCollapsibleRowGroups: boolean;
  @Input() rowGroupHeaderColumn: string;
  @Input() isARowGroupHeaderRow: Function;
  @Input() fieldToFilterOn: any;
  @Input() minimumColumnWidth: number;
  @Input() pageSize: number = 50;
  @ViewChild('sotiTable', { static: false }) sotiTable: any;
  @ViewChild('titleDiv', { static: false }) titleHeader: ElementRef;

  defaultColDef: ColDef;
  columnDefs: ColDef[];
  rowData = [];
  gridOptions = <GridOptions>{};
  modules: Module[] = [AllCommunityModule];
  private gridApi: GridApi;
  private filteredRowGroups: any[] = [];

  constructor() {
    this.defaultColDef = { resizable: true };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.tableRawData) {
      this.processData(changes.tableRawData.currentValue);
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.setGridOption('paginationPageSize', this.pageSize);

    if (this.hasCollapsibleRowGroups) {
      this.gridOptions.isExternalFilterPresent = this.isExternalFilterPresent.bind(this);
      this.gridOptions.doesExternalFilterPass = this.doesExternalFilterPass.bind(this);
    }
  }

  onGridSizeChanged(e: GridSizeChangedEvent) {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  get isPaginationEnabled(): boolean {
    return this.rowData?.length > this.pageSize;
  }

  private processData(data: any): void {
    // Assign table column & Title
    if (data && data.properties) {
      this.columnDefs = [];

      Object.values(data.properties).forEach(item => {
        if (item['key'] !== 'RowType') {
          // Temporarily map (mismatched) rows to properties
          let fieldName;
          if (item['key'] === 'VersionNumber') {
            fieldName = 'Version';
          } else if (item['key'] === 'IsMandatory') {
            fieldName = 'InstallMethod';
          } else {
            fieldName = item['key'];
          }

          this.columnDefs.push({
            field: fieldName,
            width: 100,
            minWidth: this.minimumColumnWidth,
            headerName: item['resourceCaption'],
            headerComponent: HeaderCellRendererComponent,
            cellRenderer: CellRendererComponent,
            cellRendererParams: {
              rowGroupHeaderColumn: this.rowGroupHeaderColumn,
              isRowGroupHeaderCallback: this.isARowGroupHeaderRow
                ? (row: any) => this.isARowGroupHeaderRow(row)
                : undefined,
              getIconCallback: (params: GetIconClassCallbackParams) => this.getIconForColumnType(params),
              rowGroupToggledCallback: (params: OnRowGroupToggledParams) => this.onRowGroupToggled(params),
            },
          } as ColDef);
        }
      });
    }

    // Assign table row data
    if (data) {
      this.rowData = data.rows;
      if (this.gridApi && this.isPaginationEnabled) {
        this.gridApi.paginationGoToPage(0);
      }
    }

    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 0);
  }

  private isExternalFilterPresent() {
    return this.hasCollapsibleRowGroups;
  }

  private doesExternalFilterPass(node: RowNode) {
    if (this.isARowGroupHeaderRow && this.isARowGroupHeaderRow(node)) {
      return true;
    } else {
      return this.filteredRowGroups.indexOf(node.data[this.fieldToFilterOn]) === -1;
    }
  }

  private onRowGroupToggled(params: OnRowGroupToggledParams) {
    if (params.collapsed) {
      this.filteredRowGroups.push(params.rowGroupToFilter);
    } else {
      const indexOfRowGroup = this.filteredRowGroups.indexOf(params.rowGroupToFilter);
      if (indexOfRowGroup !== -1) {
        this.filteredRowGroups.splice(indexOfRowGroup, 1);
      }
    }

    setTimeout(() => {
      this.gridApi.onFilterChanged();
    }, 0);
  }

  private getIconForColumnType(params: GetIconClassCallbackParams) {
    let iconToReturn;

    if (params.isARowGroupHeaderCell) {
      iconToReturn = 'hd-icon-toolbarpointer';
    } else {
      switch (params.columnType) {
        default: {
          iconToReturn = '';
          break;
        }
      }
    }

    return iconToReturn;
  }
}
